import { create } from 'zustand';

/**
 * Light / dark theme.
 *
 * The preference is per browser, in `localStorage` - it is a display choice,
 * not account data. It resolves to `data-theme` on <html>, which is all the CSS
 * reads (see `styles/tokens.css`). `index.html` repeats the resolution in an
 * inline script so the first paint is already in the right theme; keep the key
 * and the logic there in step with this file.
 */

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'orbit-sync.theme';
const systemLight = window.matchMedia('(prefers-color-scheme: light)');

function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function apply(preference: ThemePreference): void {
  const resolved = preference === 'system' ? (systemLight.matches ? 'light' : 'dark') : preference;
  document.documentElement.dataset.theme = resolved;
}

interface ThemeState {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  preference: readPreference(),

  setPreference(preference: ThemePreference): void {
    try {
      if (preference === 'system') {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, preference);
      }
    } catch {
      // Storage unavailable: the choice holds for this page only.
    }

    apply(preference);
    set({ preference });
  },
}));

apply(useThemeStore.getState().preference);

// "System" follows the OS live, e.g. an automatic switch at sunset.
systemLight.addEventListener('change', () => apply(useThemeStore.getState().preference));
